import { Injectable } from '@angular/core';
import { Point } from 'highcharts';

import { TranslateService } from '@ngx-translate/core';

import { AssetAllocation, AssetCategoryType } from '../../domain/public-api';
import { UtilityService } from '../../utils/utility.service';
import { PercentPipe } from '../../pipes/percent.pipe';

import type Highcharts from 'highcharts/highstock';

export type PieValueField = 'targetAllocationPct' |
'currentAllocationPct' | 'targetAllocationAmt' |
'currentAllocationAmt' | 'effectiveAllocationPct' |
'effectiveCurrentAllocationPct';
export type TooltipValueField = 'allocationPct' | 'currentAllocationPct';

type ExtendedPoint = Point & {tdWbTooltipValueField: TooltipValueField};

@Injectable({
  providedIn: 'root'
})
export class AssetAllocationPieChartService {

  private static readonly NO_DATA_BORDER_COLOUR: string = '#cccccc';
  private static readonly ASSET_CATEGORY_COLOR: Map<AssetCategoryType, string> = new Map<AssetCategoryType, string>([
    [AssetCategoryType.CANADIAN_EQUITIES, '#008a00'],
    [AssetCategoryType.US_EQUITIES, '#439d2f'],
    [AssetCategoryType.INTERNATIONAL_EQUITIES, '#719603'],
    [AssetCategoryType.CANADIAN_FIXED_INCOME, '#933214'],
    [AssetCategoryType.INTERNATIONAL_FIXED_INCOME, '#c47800'],
    [AssetCategoryType.CASH, '#ad842a'],
    [AssetCategoryType.OTHER, '#31728c'],
    [AssetCategoryType.UNCLASSIFIED, '#4b94b9']
  ]);

  constructor(private percentPipe: PercentPipe,
              private translateService: TranslateService) {
  }

  createChartConfiguration(assetAllocations: Array<AssetAllocation>,
                           pieValueField: PieValueField,
                           tooltipValueField: TooltipValueField | undefined,
                           tooltipValueFormat: string,
                           size: number,
                           innerSize: number | string,
                           animation: boolean): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    assetAllocations = this.filterAllocations(assetAllocations, pieValueField);
    const config: Highcharts.Options = {
      accessibility: {
        enabled: true
      },
      chart: {
        backgroundColor: '#ffffff',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: 'pie',
        // renderTo: renderTo,  TODO vpopov Tooltip doesn't work with renderTo
        margin: [0, 0, 0, 0],
        spacingTop: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
        height: size,
        width: size
      },
      title: {
        text: ' '
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          dataLabels: {
            enabled: false
          },
          colors: this.getPieChartColors(assetAllocations),
          borderWidth: 2,
          borderRadius: 0
        },
        series: {
          states: {
            inactive: {
              opacity: 1
            },
            hover: {
              halo: {
                size: 0
              },
              enabled: false
            }
          }
        }
      },
      series: [{
        name: 'Asset Allocation',
        type: 'pie',
        data: this.getSeriesDataPoints(assetAllocations, pieValueField, tooltipValueField)
      } as Highcharts.SeriesOptionsType]
    };
    if (this.isEmpty(assetAllocations)) {
      config.tooltip = { enabled: false };
    } else {
      config.tooltip = {
        formatter(): string {
          const value = (this as ExtendedPoint)?.tdWbTooltipValueField === undefined ?
            this.y :
            (this as ExtendedPoint)?.tdWbTooltipValueField;
          const args = {
            category: this?.name || '',
            allocation: self.percentPipe.transform(value as number, { format: tooltipValueFormat })
          };
          return self.translateService.instant('tdWb.components.assetAllocationChart.TOOLTIP', args);
        }
      };
    }
    if (this.isEmpty(assetAllocations)) {
      config.plotOptions.pie.borderColor = AssetAllocationPieChartService.NO_DATA_BORDER_COLOUR;
    }
    if (innerSize !== undefined) {
      config.plotOptions.pie.innerSize = innerSize;
    }
    if (animation === true) {
      config.plotOptions.series.animation = {
        duration: 1000
      };
    }
    if (!this.isEmpty(assetAllocations) && assetAllocations.length === 1)  {
      config.plotOptions.pie.endAngle = 359.7; // to have gap when allocation 100%
    }

    return config;
  }

  public getAssetCategoryColor(assetCategoryType: AssetCategoryType): string {
    return AssetAllocationPieChartService.ASSET_CATEGORY_COLOR.get(assetCategoryType);
  }

  public filterAllocations(assetAllocations: Array<AssetAllocation>,
                           pieValueField: PieValueField): Array<AssetAllocation> {
    return assetAllocations
      .filter((allocation: AssetAllocation): boolean => allocation[pieValueField] !== 0 )
      .slice();
  }

  private getSeriesDataPoints(assetAllocations: Array<AssetAllocation>,
                              pieValueField: PieValueField,
                              tooltipValueField: TooltipValueField): Array<Highcharts.PointOptionsObject> {
    const dataPoints: Array<Highcharts.PointOptionsObject> = new Array<Highcharts.PointOptionsObject>();
    if (this.isEmpty(assetAllocations)) {
      dataPoints.push({
        name: ' ',
        y: 100
      });
    } else {
      for (const allocation of assetAllocations) {
        const extendedDataPoint = {
          name: this.translateService.instant('tdWb.components.assetAllocationChart.assetCategoryType.' + allocation.assetCategoryType),
          y: allocation[pieValueField]
        };
        if (tooltipValueField !== undefined) {
          extendedDataPoint['tdWbTooltipValueField'] = allocation[tooltipValueField];
        }
        dataPoints.push(extendedDataPoint);
      }
    }
    return dataPoints;
  }

  private getPieChartColors(assetAllocations: Array<AssetAllocation>): Array<string> {
    const colors: Array<string> = new Array<string>();
    for (const allocation of assetAllocations) {
      colors.push(this.getAssetCategoryColor(allocation.assetCategoryType));
    }
    return colors;
  }

  private isEmpty(assetAllocations: Array<AssetAllocation>): boolean {
    return assetAllocations === undefined || UtilityService.isEmptyCollection(assetAllocations);
  }
}
